export enum ErrorCodes {
  /** @public used via the ERROR_CODES mapped type */
  FATAL = 'FATAL',
  REQUEST_UNAUTHORIZED = 'REQUEST_UNAUTHORIZED',
  REQUEST_DENIED = 'REQUEST_DENIED',
  REQUEST_INVALID = 'REQUEST_INVALID',
  METHOD_NOT_ALLOWED = 'METHOD_NOT_ALLOWED',
  MISSING_REQUIRED_PARAMETERS = 'MISSING_REQUIRED_PARAMETERS',
  MISSING_REQUIRED_PROPERTIES = 'MISSING_REQUIRED_PROPERTIES',
  UNPROCESSABLE_ENTITY = 'UNPROCESSABLE_ENTITY',
  PROVIDER_FAILED_UNRECOVERABLE = 'PROVIDER_FAILED_UNRECOVERABLE',
  MISSING_ENV_VARIABLE = 'MISSING_ENV_VARIABLE',
  CONTENTFUL_ERROR = 'CONTENTFUL_ERROR',
  INVALID_BASKET_CONTENTS = 'INVALID_BASKET_CONTENTS',
  NOT_FOUND = 'NOT_FOUND',
  /** @public used via the ERROR_CODES mapped type */
  FLIGHT_RESULTS_ERROR = 'FLIGHT_RESULTS_ERROR',
  ORDER_NEEDS_TO_BE_RESHOPPED = 'ORDER_NEEDS_TO_BE_RESHOPPED',
  OFFER_NOT_FOUND = 'OFFER_NOT_FOUND',
}

export enum ErrorKinds {
  BAD_REQUEST = 'BAD_REQUEST',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  METHOD_NOT_ALLOWED = 'METHOD_NOT_ALLOWED',
  UNPROCESSABLE_CONTENT = 'UNPROCESSABLE_CONTENT',
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
}

/** Our services have error codes/types formatted as XX_123 where XX is a two letter identifier of the service raising the error and 123 represents the relevant code */
export type ErrorCode = {
  /** http status code of the error response 4xx - 5xx */
  httpStatus: number
  code: string
  /** Text descriptor of the status, maps directly to the status code e.g. INTERNAL_SERVER_ERROR */
  kind: string
  /** Text descriptor similar to type but more specific to the cause e.g. TOKEN_EXPIRED */
  subtype?: string
  /** A written description of what this error type means */
  description?: string
  /** Used as a error message that would be returned from a response with minimal reasoning */
  businessMessage: string
  /** Used to debug the specific cause of the error via logging */
  developerMessage: string
  /** Origin of the error, eg. internal-iagl-reward-platform-fe, internal-iagl-contentful, external-google-maps-api */
  system: string
  unrecoverable?: boolean
}

export const LOG_SOURCE_LABELS = {
  // Error was logged from the bff layer
  bff: 'BFF',
  // Error was logged from the FE next server
  next: 'NextServer',
  // Error was logged from the FE next server when fetching serverside props
  next_page_props: 'NextServerSideProps',
  // Error was logged from the FE auth0 module in the next server
  auth_b2c: 'Auth0-B2C',
  // Error was logged from the BE auth0 module which could be in the bff or next layer
  auth_m2m: 'Auth0-M2M',
} as const

const fatalErrorMessage =
  'Fatal error has occurred, please refresh the application and contact support if error persists'

const defaultBusinessMessage = 'Something went wrong, please check the details and try again'

export const DEFAULT_INTERNAL_SYSTEM = 'internal-iagl-reward-platform-fe'

// Codes comply with GLP API codes as much as possible, see https://docs.iagloyalty.com/docs/
export const ERROR_CODES: { [key in ErrorCodes]: ErrorCode } = {
  FATAL: {
    httpStatus: 500,
    code: 'RP_001',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: ErrorKinds.INTERNAL_SERVER_ERROR,
    description: 'A fatal error has occurred',
    businessMessage: fatalErrorMessage,
    developerMessage: fatalErrorMessage,
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
  REQUEST_UNAUTHORIZED: {
    httpStatus: 401,
    code: 'RP_002',
    kind: ErrorKinds.UNAUTHORIZED,
    subtype: ErrorCodes.REQUEST_UNAUTHORIZED,
    description: 'User is unauthorized to perform this action',
    businessMessage: 'You are not authorized to perform this action',
    developerMessage: 'You are not authorized to perform this action',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  REQUEST_DENIED: {
    httpStatus: 403,
    code: 'RP_003',
    kind: ErrorKinds.FORBIDDEN,
    subtype: ErrorCodes.REQUEST_DENIED,
    description: 'User is not allowed to perform this action',
    businessMessage: 'You are not allowed to perform this action',
    developerMessage: 'You are not allowed to perform this action',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  REQUEST_INVALID: {
    httpStatus: 400,
    code: 'RP_004',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: ErrorCodes.REQUEST_INVALID,
    description: 'Request data is invalid',
    businessMessage: defaultBusinessMessage,
    developerMessage: 'Bad request, some form data might be invalid',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  MISSING_REQUIRED_PARAMETERS: {
    httpStatus: 400,
    code: 'RP_005',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: ErrorCodes.MISSING_REQUIRED_PARAMETERS,
    description: 'Request is missing required parameters',
    businessMessage: defaultBusinessMessage,
    developerMessage: 'Missing some required parameters',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  MISSING_REQUIRED_PROPERTIES: {
    httpStatus: 400,
    code: 'RP_006',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: ErrorCodes.MISSING_REQUIRED_PROPERTIES,
    description: 'Response is missing required properties',
    businessMessage: defaultBusinessMessage,
    developerMessage: 'Missing some required response properties',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  METHOD_NOT_ALLOWED: {
    httpStatus: 405,
    code: 'RP_016',
    kind: ErrorKinds.METHOD_NOT_ALLOWED,
    subtype: ErrorCodes.METHOD_NOT_ALLOWED,
    description: 'The requested HTTP method is not allowed for this resource',
    businessMessage: 'Action performed is not supported for this resource',
    developerMessage: 'HTTP method not supported for the requested endpoint',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  UNPROCESSABLE_ENTITY: {
    httpStatus: 422,
    code: 'RP_007',
    kind: ErrorKinds.UNPROCESSABLE_CONTENT,
    subtype: ErrorCodes.UNPROCESSABLE_ENTITY,
    description: 'Submitted data did not pass validation',
    businessMessage:
      'Something is wrong with the submitted data, please check your form details and try again',
    developerMessage:
      'Submitted data did not pass validation, client should not repeat this request without modification. Look into validationErrors object for more details',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  NOT_FOUND: {
    httpStatus: 404,
    code: 'RP_012',
    kind: ErrorKinds.NOT_FOUND,
    subtype: ErrorCodes.NOT_FOUND,
    description: 'Requested resource was not found',
    businessMessage: 'The requested resource was not found',
    developerMessage: 'The requested resource was not found',
    system: DEFAULT_INTERNAL_SYSTEM,
  },
  // Same as fatal unless provider passed a more meaningful error
  PROVIDER_FAILED_UNRECOVERABLE: {
    httpStatus: 500,
    code: 'RP_008',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: ErrorCodes.PROVIDER_FAILED_UNRECOVERABLE,
    description: 'A fatal error has occurred',
    businessMessage: fatalErrorMessage,
    developerMessage: fatalErrorMessage,
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
  MISSING_ENV_VARIABLE: {
    httpStatus: 500,
    code: 'RP_009',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: ErrorCodes.MISSING_ENV_VARIABLE,
    description: 'System is missing an Environment Variable',
    businessMessage: defaultBusinessMessage,
    developerMessage: 'Missing environment variable',
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
  CONTENTFUL_ERROR: {
    httpStatus: 500,
    code: 'RP_010',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: ErrorCodes.CONTENTFUL_ERROR,
    description: 'Content failed to load from Contentful Delivery API',
    businessMessage:
      'Content failed to load, please refresh the application and contact support if error persists',
    developerMessage: 'Contentful request failed. Look into originalError message for more details',
    system: 'internal-iagl-contentful',
    unrecoverable: true,
  },
  INVALID_BASKET_CONTENTS: {
    httpStatus: 400,
    code: 'RP_011',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: ErrorCodes.INVALID_BASKET_CONTENTS,
    description: 'Basket contains invalid items',
    businessMessage:
      'The items in your basket are not valid, please clear your basket and try again',
    developerMessage: 'The basket contains items that are expired',
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
  FLIGHT_RESULTS_ERROR: {
    httpStatus: 500,
    code: 'RP_013',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: 'FLIGHT_SEARCH_ERROR',
    description: 'Error occurred while searching for flights',
    businessMessage: 'An error occurred while searching for flights',
    developerMessage: 'An error occurred while searching for flights.',
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: false,
  },
  ORDER_NEEDS_TO_BE_RESHOPPED: {
    httpStatus: 500,
    code: 'RP_014',
    kind: ErrorKinds.INTERNAL_SERVER_ERROR,
    subtype: ErrorCodes.ORDER_NEEDS_TO_BE_RESHOPPED,
    description: 'Basket items have changed price or are no longer available',
    businessMessage:
      "We're sorry but the items in your basket have changed price or are no longer available.",
    developerMessage:
      'The basket contains items that have changed price or are no longer available.',
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
  OFFER_NOT_FOUND: {
    httpStatus: 400,
    code: 'RP_015',
    kind: ErrorKinds.BAD_REQUEST,
    subtype: ErrorCodes.OFFER_NOT_FOUND,
    description: 'Flight offer not found',
    businessMessage: "We're sorry but the flight offer has expired and is no longer available.",
    developerMessage: 'The flight offer has expired and is no longer available.',
    system: DEFAULT_INTERNAL_SYSTEM,
    unrecoverable: true,
  },
}

export const getErrorByStatusCode = (status: number) => {
  return Object.values(ERROR_CODES).find(({ httpStatus }) => status === httpStatus)
}
